import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { formatNumber } from '../admin/format';
import { useUpgrade } from './useUpgrade';

export type UnlockNoun = 'keywords' | 'competitors' | 'gaps' | 'leads' | 'conversations' | 'rows';

/** Widths of the placeholder bars, per row — fixed so the block does not shimmer between renders. */
const SHAPES = [
  ['38%', '12%', '16%', '10%'],
  ['30%', '9%', '20%', '12%'],
  ['44%', '14%', '11%', '9%'],
];

/**
 * The rows a list has beyond what the plan shows.
 *
 * The server cuts the list (the rows are never sent), so what is drawn here is
 * placeholder shapes, blurred, under one centred line — "12 more keywords" —
 * and one outlined button. It sits where the hidden rows would have been, so
 * the vendor reads the size of what they are missing in the place they would
 * have found it.
 */
export function UnlockRows({
  count,
  noun = 'rows',
  compact = false,
  reason,
  className,
  testId = 'unlock-rows',
}: {
  /** How many rows are hidden. Nothing renders at 0. */
  count: number | null | undefined;
  noun?: UnlockNoun;
  /** Two placeholder rows and no hint line, for lists inside small cards. */
  compact?: boolean;
  /** The limit behind the cut (`maxKeywordRows`), passed to the upgrade page. */
  reason?: string;
  className?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  const upgrade = useUpgrade();
  const hidden = Math.max(0, Math.floor(Number(count) || 0));
  if (!hidden) return null;

  const rows = SHAPES.slice(0, Math.min(hidden, compact ? 2 : 3));

  return (
    <div data-testid={testId} className={cn('relative overflow-hidden border-t', className)}>
      <div aria-hidden className="pointer-events-none blur-[3px] select-none">
        {rows.map((widths, i) => (
          <div key={i} className="flex items-center gap-6 border-b px-4 py-3.5 last:border-b-0 sm:px-6">
            {widths.map((w, j) => (
              <span
                key={j}
                className={cn('h-2.5 rounded-full', j === 0 ? 'bg-foreground/15' : 'bg-muted-foreground/15', j > 0 && 'ms-auto')}
                style={{ width: w }}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-b from-card/30 via-card/80 to-card px-4 text-center">
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <Lock className="size-3.5 text-muted-foreground" aria-hidden />
          {t(`FREE_PLAN.UNLOCK_ROWS.${noun.toUpperCase()}`, { count: hidden, n: formatNumber(hidden) })}
        </p>
        {!compact && <p className="text-xs text-muted-foreground">{t('FREE_PLAN.UNLOCK_ROWS.HINT')}</p>}
        <Button variant="outline" size="sm" className="mt-0.5 h-8" onClick={() => upgrade(reason)} data-testid={`${testId}-cta`}>
          {t('FREE_PLAN.UNLOCK_WITH_PRO')}
        </Button>
      </div>
    </div>
  );
}
